import { Menu, getMenuItems, getUserBookmarks, saveUserBookmarks } from "@q-food/q-food";
import { useUserStore } from "./User";
import { translate } from "~/plugins/I18n.ts";

export const useAppSidebarStore = defineStore("AppSidebar", () => {
	const compactMode = ref(false);
	const sidebarSize = ref(10);
	const searchFilter = ref("");
	const minSidebarSize = ref(10);
	const expandedItems = ref(new Set<string>());
	const menuBookmarks = ref(new Set<string>());
	const bookmarkedMenuItems = computed(() =>
		state.value.flatMap((item) => item.children).filter((item) => menuBookmarks.value.has(item.id)),
	);

	const { state, execute } = useAsyncState(getMenuItems, [], {
		immediate: false,
		resetOnExecute: false,
		shallow: true,
	});

	const menuItems = computed(() => {
		const items = [] as Menu[];
		if (bookmarkedMenuItems.value.length > 0) {
			items.push({
				title: translate("common.words.favorites"),
				children: bookmarkedMenuItems.value,
				icon: "",
				id: "bookmarks",
			});
		}

		items.push(...state.value);

		if (!searchFilter.value) {
			return items;
		}

		const filtered = items
			.map((item) => {
				return {
					...item,
					children: item.children.filter((child) => {
						return child.title.toLowerCase().includes(searchFilter.value);
					}),
				};
			})
			.filter((item) => item.children.length > 0);

		return filtered;
	});

	/**
	 * Load user bookmarks from the backend. Bookmarks are per user, and logging in as someone else
	 * happens without a page reload, so this reloads on every user change — otherwise the next user
	 * keeps the previous user's bookmarks, and toggling one would save that set onto their account.
	 */
	watch(
		() => useUserStore().activeUser?.id,
		(userId) => {
			if (userId == null) {
				menuBookmarks.value = new Set();
				return;
			}

			getUserBookmarks().then((bookmarks) => {
				menuBookmarks.value = new Set(bookmarks);
			});
		},
		{ immediate: true },
	);

	function addMenuBookmark(menuKey: string) {
		menuBookmarks.value.add(menuKey);
		syncBookmarksDebounced();
	}

	function removeMenuBookmark(menuKey: string) {
		menuBookmarks.value.delete(menuKey);
		syncBookmarksDebounced();
	}

	const syncIntervalInMs = 500;
	/**
	 * Synchronize bookmarks with the backend with a debounce
	 */
	const syncBookmarksDebounced = useDebounceFn(() => saveUserBookmarks([...menuBookmarks.value]), syncIntervalInMs);

	function toggleExpand(id: string) {
		if (expandedItems.value.has(id)) {
			expandedItems.value.delete(id);
		} else {
			expandedItems.value.add(id);
		}
	}

	function setFilter(v: string) {
		searchFilter.value = v.toLowerCase().trim();
	}

	function clearFilter() {
		searchFilter.value = "";
	}

	function expandAll() {
		menuItems.value.forEach((item) => expandedItems.value.add(item.id));
	}

	function collapseAll() {
		expandedItems.value.clear();
	}

	function toggleCompactMode() {
		compactMode.value = !compactMode.value;
		expandAll();
		clearFilter();

		if (compactMode.value === false) {
			collapseAll();
		}
	}

	watch(searchFilter, () => {
		if (searchFilter.value) {
			expandAll();
		} else if (!compactMode.value) {
			collapseAll();
		}
	});

	return {
		compactMode: readonly(compactMode),
		expandedItems: readonly(expandedItems),
		minSidebarSize: readonly(minSidebarSize),
		sidebarSize: readonly(sidebarSize),
		filteredItems: menuItems,
		searchFilter,
		setFilter,
		clearFilter,
		expandAll,
		collapseAll,
		toggleCompactMode,
		toggleExpand,
		state,
		refreshSidebar: () => execute(),
		isMenuItemBookmarked: (menuKey: string) => menuBookmarks.value.has(menuKey),
		menuBookmarks: computed(() => Array.from(menuBookmarks.value)),
		addMenuBookmark,
		removeMenuBookmark,
	};
});
